import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Textarea } from '../../components/ds';
import api from '../../api/client';

// Two branches: join existing company (下拉選擇), or create a new one (full form).
export default function AddCompanyModal({ accountId, initialBranch = 'join', onClose, onAdded }) {
  const [branch, setBranch] = useState(initialBranch); // 'join' | 'create'

  // --- join branch state ---
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // --- create branch state ---
  const [form, setForm] = useState({
    company_name: '',
    tax_id_number: '',
    phone_area_code: '',
    phone_number: '',
    address: '',
    description: '',
    contact_name: '',
    contact_job_title: '',
    contact_email: '',
    contact_phone: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAvailable = async () => {
      try {
        const [allCompanies, accountCompanies] = await Promise.all([
          api.getCompanies(),
          api.getAccountCompanies(accountId),
        ]);
        const associatedIds = new Set((accountCompanies || []).map((c) => c.company_id));
        const available = (allCompanies || []).filter((c) => !associatedIds.has(c.uuid));
        setCompanies(available);
      } catch (err) {
        setError('載入公司清單失敗：' + err.message);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchAvailable();
  }, [accountId]);

  const selectedName = companies.find((c) => c.uuid === selected)?.company_name || '';

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError('');
  };

  const handleJoinSubmit = async () => {
    if (!selected || !selectedName) return;
    setSubmitting(true);
    setError('');
    try {
      await api.addAccountCompany(accountId, {
        company_id: selected,
        company_name: selectedName,
      });
      onAdded();
    } catch (err) {
      setError('新增公司失敗：' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSubmit = async () => {
    if (!form.company_name.trim()) {
      setError('請填寫公司名稱');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      // Body without company_id — backend AddCompany (AS-175) handles CreateCompany in PAC
      await api.addAccountCompany(accountId, {
        company_name: form.company_name.trim(),
        tax_id_number: form.tax_id_number.trim(),
        phone_area_code: form.phone_area_code.trim(),
        phone_number: form.phone_number.trim(),
        address: form.address.trim(),
        description: form.description.trim(),
        contact_name: form.contact_name.trim(),
        contact_job_title: form.contact_job_title.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
      });
      onAdded();
    } catch (err) {
      setError('建立公司失敗：' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    branch === 'join' ? !!selected : !!form.company_name.trim();

  const submitLabel =
    submitting
      ? branch === 'join' ? '加入中...' : '建立中...'
      : branch === 'join' ? '確認加入' : '建立';

  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
      <Button variant="secondary" onClick={onClose} disabled={submitting}>
        取消
      </Button>
      <Button
        onClick={branch === 'join' ? handleJoinSubmit : handleCreateSubmit}
        disabled={!canSubmit || submitting}
      >
        {submitLabel}
      </Button>
    </div>
  );

  return (
    <Modal
      title={branch === 'join' ? '加入既有 Company' : '新建 Company'}
      onClose={onClose}
      footer={footer}
    >
      {/* Branch selector tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['join', 'create'].map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => { setBranch(b); setError(''); }}
            style={{
              flex: 1,
              padding: '8px 16px',
              borderRadius: 'var(--radius-md)',
              border: branch === b
                ? '2px solid var(--primary-orange)'
                : '1px solid var(--border-color)',
              background: branch === b ? 'var(--primary-orange-tint)' : 'transparent',
              color: branch === b ? 'var(--primary-orange)' : 'var(--text-secondary)',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              transition: 'all 150ms ease',
            }}
          >
            {b === 'join' ? '加入既有 Company' : '新建 Company'}
          </button>
        ))}
      </div>

      {/* ── Join branch ── */}
      {branch === 'join' && (
        <>
          {loadingCompanies ? (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>載入中...</div>
          ) : companies.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 20 }}>
              目前無可關聯的公司
            </p>
          ) : (
            <>
              <p style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: 14 }}>
                選擇要關聯到此帳號的公司：
              </p>
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {companies.map((company) => (
                  <label
                    key={company.uuid}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      border: selected === company.uuid
                        ? '2px solid var(--primary-orange)'
                        : '1px solid var(--border-color)',
                      marginBottom: 8,
                      backgroundColor: selected === company.uuid ? 'var(--bg-secondary)' : 'transparent',
                    }}
                  >
                    <input
                      type="radio"
                      name="company"
                      checked={selected === company.uuid}
                      onChange={() => setSelected(company.uuid)}
                    />
                    <span style={{ fontWeight: 500 }}>{company.company_name}</span>
                  </label>
                ))}
              </div>
            </>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '8px 12px',
              background: 'var(--status-info-bg)',
              border: '1px solid var(--status-info-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 12,
              color: 'var(--status-info-text)',
              marginTop: 12,
            }}
          >
            <svg
              width="14" height="14" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round"
              style={{ flexShrink: 0, marginTop: 1 }}
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            加入後，此 Account 的 Portal 用戶可代操此 Company 旗下的廣告。
          </div>
        </>
      )}

      {/* ── Create branch ── */}
      {branch === 'create' && (
        <>
          {/* Company info */}
          <div style={{
            fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.05em', color: 'var(--text-tertiary)', marginBottom: 12,
          }}>
            公司基本資料
          </div>

          <Input
            label="公司名稱 *"
            value={form.company_name}
            onChange={handleFieldChange('company_name')}
            placeholder="e.g. Acme Media Ltd."
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
            <Input
              label="統一編號 (Tax ID)"
              value={form.tax_id_number}
              onChange={handleFieldChange('tax_id_number')}
              placeholder="8 碼統編"
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>區碼 + 電話</div>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 8 }}>
                <input
                  className="input-field"
                  value={form.phone_area_code}
                  onChange={handleFieldChange('phone_area_code')}
                  placeholder="02"
                  style={{
                    padding: '9px 12px',
                    fontSize: 14,
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--input-border)',
                    borderRadius: 'var(--radius-md)',
                    outline: 'none',
                    width: '100%',
                  }}
                />
                <input
                  className="input-field"
                  value={form.phone_number}
                  onChange={handleFieldChange('phone_number')}
                  placeholder="12345678"
                  style={{
                    padding: '9px 12px',
                    fontSize: 14,
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--input-border)',
                    borderRadius: 'var(--radius-md)',
                    outline: 'none',
                    width: '100%',
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: 4 }}>
            <Input
              label="地址"
              value={form.address}
              onChange={handleFieldChange('address')}
              placeholder="台北市大安區復興南路二段..."
            />
          </div>

          <div style={{ marginTop: 4 }}>
            <Textarea
              label="描述"
              value={form.description}
              onChange={handleFieldChange('description')}
              placeholder="公司簡介（選填）"
              rows={3}
            />
          </div>

          {/* Contact info */}
          <div style={{
            fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.05em', color: 'var(--text-tertiary)',
            marginTop: 20, marginBottom: 12,
            paddingTop: 16, borderTop: '1px solid var(--border-color)',
          }}>
            主要聯絡人
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Input
              label="姓名"
              value={form.contact_name}
              onChange={handleFieldChange('contact_name')}
              placeholder="John Chen"
            />
            <Input
              label="職稱 (Job Title)"
              value={form.contact_job_title}
              onChange={handleFieldChange('contact_job_title')}
              placeholder="Media Manager"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
            <Input
              label="Email"
              type="email"
              value={form.contact_email}
              onChange={handleFieldChange('contact_email')}
              placeholder="john@example.com"
            />
            <Input
              label="電話"
              value={form.contact_phone}
              onChange={handleFieldChange('contact_phone')}
              placeholder="0912-345-678"
            />
          </div>
        </>
      )}

      {error && (
        <div style={{ marginTop: 12, color: 'var(--status-error-text)', fontSize: 13 }}>
          {error}
        </div>
      )}
    </Modal>
  );
}
